import { forwardRef, FunctionComponent, Ref, PropsWithoutRef, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp } from '../../theme';
import Flex from '../Flex';
import { ForwardProps, BaseProps, AsProp } from '../../types';
import { StyledPopover } from '../Popover';

import { StyledCardMedia } from './CardMedia';

export interface CardProps extends BaseProps, AsProp {
  /** Components and content for the Card. */
  children?: ReactNode;
  /**
   * Enables the focus, hover, and active styles.
   * @default false
   * @deprecated
   */
  interactive?: boolean;
  /** Callback for onClick event. */
  onClick?: (event: any) => void;
  /**
   * HTML role attribute for accessibility.
   * @default "region"
   */
  role?: string;
}

const defaultProps: Partial<CardProps> = {
  interactive: false,
  role: 'region'
};

export const StyledCard = styled.article<Partial<CardProps>>(props => {
  const {
    'border-radius': borderRadius,
    'border-color': borderColor,
    background
  } = props.theme.components.card;
  const interactive = props.interactive
    ? css`
        &:hover {
          border: 0;
          box-shadow: 0 0 0 0.0625rem ${borderColor};
        }
        &:focus {
          border: 0;
          box-shadow: 0 0 0 0.125rem ${borderColor};
        }
      `
    : undefined;

  return css`
    ${StyledPopover} &, &:not(& &) {
      background-color: ${background};
      border-radius: ${borderRadius};

      &:focus {
        outline: none;
      }

      ${interactive}

      ${StyledCardMedia} {
        &:first-child img {
          border-radius: ${borderRadius} ${borderRadius} 0 0;
        }
      }
    }
  `;
});

StyledCard.defaultProps = defaultThemeProp;

const Card: FunctionComponent<CardProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<CardProps>, ref: Ref<HTMLElement>) => {
    const { children, as, onClick, interactive, role, ...restProps } = props;

    const interactiveProps = {
      ...(onClick && {
        onClick,
        tabIndex: 0,
        role: 'button'
      }),
      ...(interactive && {
        tabIndex: 0
      }),
      ...(role && { role }),
      interactive
    };

    return (
      <Flex
        container={{ direction: 'column' }}
        as={StyledCard}
        forwardedAs={as}
        ref={ref}
        {...interactiveProps}
        {...restProps}
      >
        {children}
      </Flex>
    );
  }
);

Card.defaultProps = defaultProps;

export default Card;
